import React from 'react';
import { motion } from 'framer-motion';
import {
  Target,
  Lightbulb,
  Users,
  Brain,
  ArrowRight,
  CheckCircle2,
  Rocket,
  Scale,
  Heart,
  RefreshCw,
  GraduationCap,
  Handshake,
  FileText
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
//import RecruitmentBanner from '../components/utilities/RecruitmentBanner';

const GroupPracticePage: React.FC = () => {
  const values = [
    {
      icon: <Brain size={24} />,
      title: "Reflexivity",
      description: "Ensuring therapists reflect on their practices and growth"
    },
    {
      icon: <Rocket size={24} />,
      title: "Entrepreneurial Spirit",
      description: "Encouraging innovation and ownership among team members"
    },
    {
      icon: <Scale size={24} />,
      title: "Transparency",
      description: "Maintaining open and honest communication across all operations"
    },
    {
      icon: <Heart size={24} />,
      title: "Self-Care Priority",
      description: "Placing therapist well-being at the forefront"
    },
    {
      icon: <RefreshCw size={24} />,
      title: "Mutual Accountability",
      description: "Juniors and Seniors can hold each other accountable"
    },
    {
      icon: <GraduationCap size={24} />,
      title: "Individual Growth",
      description: "Personal development contributes to organizational success"
    },
    {
      icon: <Handshake size={24} />,
      title: "Collaboration First",
      description: "Building a supportive rather than competitive environment"
    },
    {
      icon: <Users size={24} />,
      title: "Empowerment",
      description: "The organization works for therapists, not vice versa"
    }
  ];

  const roles = [
    {
      title: "Senior Therapist",
      responsibilities: [
        "Supervise cases individually and in groups",
        "Support professional growth",
        "Maintain caseload",
        "Refer clients as needed"
      ]
    },
    {
      title: "Senior Track Therapist",
      responsibilities: [
        "Shadow senior therapists",
        "Learn specialized therapy skills",
        "Assist in team management",
        "Develop supervision skills"
      ]
    },
    {
      title: "Associate Therapist",
      responsibilities: [
        "1-year renewable tenure",
        "Focus on skill-building",
        "Engage in upskilling",
        "Develop private practice"
      ]
    }
  ];

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Group Practice | Mental Health Professionals Community | Greh Pravesh Therapy</title>
        <meta name="description" content="Supportive community for mental health professionals. Build sustainable practice with therapist-focused growth. Call 8591049845 now." />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Group Practice | Mental Health Professionals Community" />
        <meta
          property="og:description"
          content="Join a supportive ecosystem for therapists focused on professional development, collaboration, and individual growth."
        />
        <meta property="og:type" content="professional-service" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Group Practice | Therapist Community" />
        <meta
          name="twitter:description"
          content="Redefining mental health spaces with a community-driven approach that empowers therapists and supports their individual journeys."
        />

        {/* JSON-LD Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Greh Pravesh Therapy Group Practice",
            "description": "A collaborative community for mental health professionals focused on sustainable growth and support",
            "provider": {
              "@type": "Organization",
              "name": "Greh Pravesh Therapy",
              "specialty": "Mental Health Professional Development"
            },
            "availableService": [
              {
                "@type": "Service",
                "name": "Senior Therapist Role",
                "description": "Supervise cases, support professional growth, maintain caseload"
              },
              {
                "@type": "Service",
                "name": "Associate Therapist Role",
                "description": "Skill-building tenure with focus on professional development"
              }
            ]
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-teal-50">

        {/* Hero Section - Repositioned for better flow */}
        <section className="relative py-20 overflow-hidden bg-gradient-to-br from-amber-300 to-white">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 20% 20%, rgba(245, 158, 11, 0.1) 0%, transparent 40%)`,
              backgroundSize: '100% 100%'
            }}
          />
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold text-teal-900 mb-6">
                  Join Our Group Practice
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Redefining the mental health space to build supportive, non-exploitative communities for therapists, while paving a safe and self-sustaining path for future generations.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="#roles" className="inline-flex items-center px-6 py-3 bg-orange-400 text-white rounded-xl hover:bg-orange-500 transition-colors shadow-md">
                    Learn More <ArrowRight className="ml-2" size={20} />
                  </a>
                  <a
                    href="https://docs.google.com/document/d/10HrQVp1-6y4506Tcvte6wKxx0hp5ogZxBKhXBktdU5Y/edit?tab=t.0#heading=h.p0qnh951oav"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 border-2 border-teal-600 text-teal-700 rounded-xl hover:bg-teal-50 transition-colors"
                  >
                    <FileText className="mr-2" size={20} />
                    View Full Document
                  </a>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <img
                  src="/images/group-2.png"
                  alt="Group Practice Community"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* <RecruitmentBanner /> */}

        {/* What is Group Practice Section */}
        <section className="py-20 bg-white/90">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-teal-900 mb-6">What is Group Practice?</h2>
              <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full mb-8" />
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A collaborative community where mental health professionals work together while maintaining their individual identity. We're building a supportive ecosystem that prioritizes therapist well-being and sustainable growth.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl shadow-lg border border-teal-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-teal-700" />
                  </div>
                  <h3 className="text-2xl font-bold text-teal-900">Our Vision</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Redefining the mental health space to build supportive, non-exploitative communities for therapists, while paving a safe and self-sustaining path for future generations in the field. We create an environment where professionals can work in a team without losing their individual essence.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl shadow-lg border border-orange-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-orange-700" />
                  </div>
                  <h3 className="text-2xl font-bold text-teal-900">Our Mission</h3>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p className="leading-relaxed">
                    We are committed to:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-orange-400 rounded-full mr-3"></span>
                      Mobilizing a community to take charge of their livelihoods
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-orange-400 rounded-full mr-3"></span>
                      Uniting well-established and fair supervisor-therapist practices to start a community-led movement
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section - Enhanced with hover effects */}
        <section className="py-20 bg-gradient-to-br from-teal-50 to-white">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-teal-900 mb-6">Our Core Values</h2>
              <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full mb-4" />
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These principles guide our approach to mental health practice and community building.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-xl bg-white shadow-md border border-gray-100 hover:border-orange-200 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center text-teal-700 mb-4 group-hover:bg-teal-200 transition-colors">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-teal-900 mb-2 group-hover:text-orange-500 transition-colors">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Roles Section - Enhanced with better ID linking and styling */}
        <section id="roles" className="py-20 bg-white/80">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-teal-900 mb-6">Available Roles</h2>
              <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full mb-8" />
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore our different therapist positions and find the right fit for your career stage.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {roles.map((role, index) => (
                <motion.div
                  key={role.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-8 rounded-xl bg-white shadow-lg border border-gray-100 hover:border-teal-200 hover:shadow-xl transition-all duration-300"
                >
                  <h3 className="text-2xl font-bold text-teal-900 mb-4">{role.title}</h3>
                  <ul className="space-y-3 mb-6">
                    {role.responsibilities.map((resp, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <CheckCircle2 className="flex-shrink-0 w-5 h-5 text-orange-400 mt-1" />
                        <span className="text-gray-600">{resp}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/roles/${role.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className="mt-6 inline-flex items-center justify-center px-6 py-2 rounded-lg bg-teal-600 hover:bg-orange-500 text-white font-medium transition-colors duration-200 shadow-md w-full"
                  >
                    <span>Learn more</span>
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Preview Section - Enhanced CTA */}
        <section className="py-20 bg-gradient-to-br from-teal-900 to-teal-800 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Common Questions</h2>
              <div className="w-24 h-1 bg-orange-400 mx-auto rounded-full mb-8" />
              <p className="text-xl text-white/80 max-w-2xl mx-auto">
                Find answers to common questions about joining our group practice, supervision, fees, and more.
              </p>
            </motion.div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <a
                href="/join-us"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-900 rounded-xl hover:bg-orange-400 hover:text-white transition-colors font-medium shadow-lg"
              >
                Join Us Now <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="https://docs.google.com/document/d/10HrQVp1-6y4506Tcvte6wKxx0hp5ogZxBKhXBktdU5Y/edit?tab=t.0#heading=h.p0qnh951oav"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-teal-800 border border-white/30 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium"
              >
                <FileText className="mr-2" size={20} />
                View Full Document
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GroupPracticePage;
